import {
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { useHistogramToXYColorRules } from './hooks/use-histogram-to-xy-color-rules.js';
import { transformHistogramAnnotationsToXY } from './transformer-annotations-to-xy.js';
import { transformHistogramDataToXYChart } from './transformer-data-to-xy.js';
import {
  mapSelectedSeriesToHistogram,
  mapSelectionDomainToHistogramDomains,
} from './transformer-selection-to-histogram.js';
import { mapHistogramSeriesActionsToXY } from './transformer-series-actions-to-xy.js';
import { mapHistogramThresholdsToXY } from './transformer-thresholds-to-xy.js';
import { buildYAxisProps } from './utils/axis-props-mapping.js';
import { buildHistogramImperativeHandle } from './utils/build-histogram-imperative-handle.js';
import { buildYAxesConfigFromConfig } from './utils/build-y-axes-config.js';
import { defineAxisId } from './utils/define-axis-id.js';
import { deriveXYChartPropsFromConfig } from './utils/derive-xychart-props-from-config.js';
import { formatCSV } from './utils/format-csv.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { downloadCSV } from '../../../core/utils/download.js';
import { getChartStatesTemplates } from '../../core/components/chart-states/utils/get-chart-states-template.js';
import { CSS_DOWNLOAD_FILENAME } from '../../core/constants.js';
import { useHistogramChartConfig } from '../../histogram/hooks/useHistogramChartConfig.js';
import type { HistogramChartProps } from '../../histogram/types/histogram-props.js';
import type { HistogramChartRef } from '../../histogram/types/histogram-ref.js';
import type { XYChartRef } from '../../xy-chart/types/xy-chart.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { renderXYAnnotations } from '../shared/utils/render-xy-annotations.js';

export const HistogramToXY = forwardRef<
  HistogramChartRef,
  PropsWithChildren<HistogramChartProps>
>((props, forwardedRef) => {
  const {
    data: propsData,
    loading,
    children,
    className,
    style,
    width,
    height,
    colorPalette,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    truncationMode,
    valueRepresentation,
    seriesActions,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  } = props;

  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);

  const config = useHistogramChartConfig(children, {
    colorPalette,
    truncationMode,
    valueRepresentation,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  });

  const { barSeriesProps, units, seriesColors } =
    transformHistogramDataToXYChart(propsData);

  const allColorRules = useHistogramToXYColorRules({
    data: propsData,
    seriesColors,
    colorRules: config.colorRules,
  });

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const downloadDataCallback = useCallback(() => {
    const csvString = formatCSV(propsData);
    downloadCSV(csvString, CSS_DOWNLOAD_FILENAME);
  }, [propsData]);

  const selectActions = config.select?.actions;
  const mappedSeriesActions = useMemo(
    () =>
      seriesActions
        ? mapHistogramSeriesActionsToXY(seriesActions, propsData)
        : undefined,
    [seriesActions, propsData],
  );

  const yAxesConfig = buildYAxesConfigFromConfig(config);

  const mappedThresholds = useMemo(
    () => mapHistogramThresholdsToXY(config.thresholds, [...units]),
    [config.thresholds, units],
  );

  const {
    xAxisConfig,
    disableZoom,
    disablePan,
    toolbarEnabled,
    toolbarPlacement,
    toolbarIntents,
    onZoomChange: effectiveOnZoomChange,
  } = deriveXYChartPropsFromConfig(config, onZoomChange);

  const xyRef = useRef<XYChartRef>(null);

  useImperativeHandle(
    forwardedRef,
    () => buildHistogramImperativeHandle(xyRef, config),
    [config],
  );

  const annotations = config.annotations;
  const xAxisId = 'x-axis';
  // 'grouped' is a Histogram-only variant — XYChart only knows 'single' | 'shared'
  const tooltipVariant =
    config.tooltip.variant === 'single' ? 'single' : 'shared';

  return (
    <XYChart
      data={[]}
      ref={xyRef}
      loading={loading}
      className={className}
      style={style}
      width={width}
      height={height}
      colorPalette={config.colorPalette}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      truncationMode={config.truncationMode}
      __downloadDataCallback={downloadDataCallback}
      data-dt-component="HistogramChart"
      {...behaviorTrackingProps}
    >
      <XYChart.Tooltip _variant={tooltipVariant} />
      {errorState && <XYChart.ErrorState>{errorState}</XYChart.ErrorState>}
      {emptyState && <XYChart.EmptyState>{emptyState}</XYChart.EmptyState>}
      {selectActions ? (
        <XYChart.Select
          actions={(selectedSeries, selectionDomain) =>
            selectActions(
              mapSelectedSeriesToHistogram(selectedSeries),
              mapSelectionDomainToHistogramDomains(selectionDomain),
            )
          }
        />
      ) : null}
      {barSeriesProps.map(({ data, unit }, index) => (
        <XYChart.BarSeries
          key={index}
          data={data}
          xAxisId={xAxisId}
          yAxisId={defineAxisId(unit)}
          x0Accessor="x0"
          x1Accessor="x1"
          y1Accessor="y1"
          variant={
            config.valueRepresentation === 'relative' && propsData.length === 1
              ? 'single'
              : 'stack'
          }
          seriesIdAccessor="seriesId"
          nameAccessor="name"
          actions={mappedSeriesActions}
        />
      ))}
      <XYChart.XAxis
        id={xAxisId}
        type={xAxisConfig.scale === 'log' ? 'log' : 'numerical'}
        position="bottom"
        disableZoom={disableZoom}
        disablePan={disablePan}
        initialZoom={config.initialZoom as [number, number]}
        currentZoom={config.currentZoom as [number, number]}
        onZoomChange={effectiveOnZoomChange}
        infiniteZoom={config.infiniteZoom}
        {...xAxisConfig}
      />
      {[...units].map((unit, index) => (
        <XYChart.YAxis
          key={defineAxisId(unit)}
          {...buildYAxisProps(
            index,
            unit,
            yAxesConfig,
            config.valueRepresentation,
          )}
        />
      ))}
      <XYChart.Legend {...config.legend} />
      {allColorRules.map((rule) => (
        <XYChart.ColorRule
          key={`${rule.comparator}-${rule.matchValue}-${rule.valueAccessor}`}
          {...rule}
        />
      ))}
      {mappedThresholds.map((threshold) => (
        <XYChart.ThresholdIndicator
          key={`${threshold.yAxisId}-${threshold.color}-${
            threshold.label ?? ''
          }`}
          {...threshold}
        />
      ))}
      <XYChart.Toolbar hidden={!toolbarEnabled} placement={toolbarPlacement} />
      {toolbarIntents?.map((intentProps, index) => (
        <XYChart.Intent key={index} {...intentProps} />
      ))}
      {annotations &&
        renderXYAnnotations(
          transformHistogramAnnotationsToXY(annotations.tracks),
          xAxisId,
          annotations,
        )}
    </XYChart>
  );
});

(
  HistogramToXY as typeof HistogramToXY & {
    displayName: string;
  }
).displayName = 'HistogramToXY';
